/**
 * Deterministic seed data for the in-memory adapter.
 *
 * Only ever imported by `adapter.ts` (docs/06-component-refactor.md §7.8).
 * Pages and query hooks must not reach in here — the adapter is the single
 * swap point for the real API.
 *
 * Meridian State University is the fully-populated client; the other two
 * exist so client switching, the client list sort, and the cache-purge
 * invariant all have something real to exercise.
 */
import type {
  Client,
  CopyDoc,
  CrawledPage,
  CrawlRun,
  DateProvenance,
  FacultyMember,
  MediaDetail,
  PartnerFact,
  ProgramDetail,
  ProgramType,
  QuoteDetail,
  ReviewStatus,
  Segment,
  SlateProgram,
  WbProgram,
  Verdict,
} from '@/types'

const REVIEWER = 'joe@waybettermarketing.com'

function daysAgo(n: number, hour = 9): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - n)
  d.setUTCHours(hour, 0, 0, 0)
  return d.toISOString()
}

/** Stable pseudo-hash so reloads produce the same entity hashes. */
function hashFor(seed: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return (h.toString(16) + seed.length.toString(16)).padEnd(16, '0').slice(0, 16)
}

function verdictsFor(id: string, status: ReviewStatus, at: string): Verdict[] {
  if (status === 'pending') return []
  return [{ at, by: REVIEWER, verdict: status, entityHash: hashFor(id) }]
}

/* --------------------------------------------------------------- clients */

const MERIDIAN = 'cl_meridian'
const NORTHBAY = 'cl_northbay'
const CEDARWOOD = 'cl_cedarwood'

export const clients: Client[] = [
  {
    id: MERIDIAN,
    name: 'Meridian State University',
    primaryDomain: 'meridianstate.edu',
    additionalDomains: ['admissions.meridianstate.edu', 'news.meridianstate.edu'],
    includePatterns: [
      'https://meridianstate.edu/programs/**',
      'https://meridianstate.edu/admissions/**',
      'https://admissions.meridianstate.edu/**',
    ],
    excludePatterns: ['https://meridianstate.edu/**/archive/**', 'https://meridianstate.edu/events/**'],
    depthLimit: 3,
    pageLimit: 10000,
    cadence: 'monthly',
    pipelineStage: 'extracted',
    lastCrawlAt: daysAgo(2, 6),
    lastCrawlRunId: 'run_meridian_04',
    createdAt: daysAgo(140),
    updatedAt: daysAgo(2, 7),
  },
  {
    id: NORTHBAY,
    name: 'Northbay Health Partners',
    primaryDomain: 'northbayhealth.org',
    additionalDomains: [],
    includePatterns: ['https://northbayhealth.org/services/**'],
    excludePatterns: [],
    depthLimit: 2,
    pageLimit: 10000,
    cadence: 'quarterly',
    pipelineStage: 'ready',
    lastCrawlAt: daysAgo(19, 4),
    lastCrawlRunId: 'run_northbay_02',
    createdAt: daysAgo(210),
    updatedAt: daysAgo(16),
  },
  {
    id: CEDARWOOD,
    name: 'Cedarwood Senior Living',
    primaryDomain: 'cedarwoodliving.com',
    additionalDomains: [],
    includePatterns: [],
    excludePatterns: [],
    depthLimit: 3,
    pageLimit: 10000,
    cadence: 'manual',
    pipelineStage: 'crawled',
    lastCrawlAt: daysAgo(5, 11),
    lastCrawlRunId: 'run_cedarwood_01',
    createdAt: daysAgo(38),
    updatedAt: daysAgo(5, 12),
  },
]

/* --------------------------------------------------------- program types */

/** Global, like `content.program_type`. Illustrative — the real list is not seeded yet. */
export const programTypes: ProgramType[] = [
  { id: 'pt_major_ug', name: 'Major (Undergrad)', isMajor: true, isMinor: false, isUndergrad: true, isGrad: false },
  { id: 'pt_major_grad', name: 'Major (Grad)', isMajor: true, isMinor: false, isUndergrad: false, isGrad: true },
  { id: 'pt_minor_ug', name: 'Minor (Undergrad)', isMajor: false, isMinor: true, isUndergrad: true, isGrad: false },
  { id: 'pt_cert_ug', name: 'Certificate (Undergrad)', isMajor: false, isMinor: false, isUndergrad: true, isGrad: false },
  { id: 'pt_cert_grad', name: 'Certificate (Grad)', isMajor: false, isMinor: false, isUndergrad: false, isGrad: true },
]

/* -------------------------------------------------------------- segments */

/**
 * Subject groupings, not degree levels. Sizes vary on purpose: a segment can
 * hold a single program.
 */
const SEGMENT_SEEDS: Pick<Segment, 'id' | 'clientId' | 'name' | 'position'>[] = [
  { id: 'sg_health', clientId: MERIDIAN, name: 'Health Sciences', position: 0 },
  { id: 'sg_computing', clientId: MERIDIAN, name: 'Computing & Data', position: 1 },
  { id: 'sg_business', clientId: MERIDIAN, name: 'Business', position: 2 },
  { id: 'sg_education', clientId: MERIDIAN, name: 'Education', position: 3 },
  { id: 'sg_social', clientId: MERIDIAN, name: 'Social & Behavioral Sciences', position: 4 },
  { id: 'sg_legal', clientId: MERIDIAN, name: 'Legal Studies', position: 5 },
  { id: 'sg_svc_primary', clientId: NORTHBAY, name: 'Primary care', position: 0 },
  { id: 'sg_svc_specialty', clientId: NORTHBAY, name: 'Specialty care', position: 1 },
]

export const segments: Segment[] = SEGMENT_SEEDS.map((s) => ({
  ...s,
  headline: s.id === 'sg_health' ? 'Care that starts in the clinic' : null,
  description: null,
  featuredQuoteId: null,
  featuredQuote: null,
  facultyMemberId: s.id === 'sg_health' ? 'fm_okafor' : null,
  facultyMember: null,
  imageAssetId: null,
  imageUrl: null,
}))

/* -------------------------------------------------------- faculty members */

export const faculty: FacultyMember[] = [
  {
    id: 'fm_okafor',
    clientId: MERIDIAN,
    name: 'Dr. Ada Okafor',
    email: 'aokafor@meridian.edu',
    imageUrl: null,
    active: true,
    createdAt: '2026-10-01T00:00:00Z',
    updatedAt: '2026-10-01T00:00:00Z',
  },
  {
    id: 'fm_lind',
    clientId: MERIDIAN,
    name: 'Prof. Sam Lind',
    email: null,
    imageUrl: null,
    active: false,
    createdAt: '2026-10-01T00:00:00Z',
    updatedAt: '2026-10-01T00:00:00Z',
  },
]

/* -------------------------------------------------------------- programs */

/**
 * `name` is as crawled, without the credential (the programs agent strips it);
 * `wbProgram` is the WB program it sits under and defaults to the name: seeds
 * with the same one share a WB program (ADR-0019). `unassigned` rows have none.
 * `mergedInto` rows are duplicates the consolidation step pointed at a
 * survivor (migrations/022); like the pipeline, they get no WB program. Nor do
 * graduate rows: WB programs are undergraduate (PC-25, ADR-0022).
 */
interface ProgramSeed {
  id: string
  name: string
  wbProgram?: string
  unassigned?: boolean
  degree: string | null
  level: string | null
  department?: string
  description: string | null
  segmentId: string | null
  programTypeId: string | null
  path: string
  /** The program's own page when it is not `path`; null when none was linked. */
  programPath?: string | null
  status: ReviewStatus
  favorite?: boolean
  flagged?: boolean
  tags?: string[]
  previouslyRejected?: boolean
  slate?: string
  mergedInto?: string
  credentialLength?: string
  deliveryModes?: string[]
  concentrations?: string[]
}

const PROGRAM_SEEDS: ProgramSeed[] = [
  {
    id: 'pg_nursing_bsn',
    name: 'Nursing',
    wbProgram: 'Nursing (pre-licensure)',
    degree: 'BSN',
    level: 'undergraduate',
    department: 'School of Nursing',
    description:
      'Four-year pre-licensure nursing degree with clinical placements from the second year. Graduates sit the NCLEX-RN in their final semester.',
    segmentId: 'sg_health',
    programTypeId: 'pt_major_ug',
    path: '/programs/nursing/bsn',
    status: 'approved',
    favorite: true,
    tags: ['health', 'flagship'],
    slate: 'sp_nursing_bsn',
    credentialLength: 'four years',
    deliveryModes: ['on_campus'],
  },
  {
    id: 'pg_nursing_rnbsn',
    name: 'RN-to-BSN Completion',
    degree: 'BSN',
    level: 'undergraduate',
    department: 'School of Nursing',
    description:
      'Online degree completion for working registered nurses. Twelve to eighteen months depending on transfer credit.',
    segmentId: 'sg_health',
    programTypeId: 'pt_major_ug',
    path: '/programs/nursing/rn-to-bsn',
    status: 'pending',
    tags: ['health', 'online'],
    slate: 'sp_nursing_bsn',
    credentialLength: '12–18 months',
    deliveryModes: ['online'],
  },
  {
    id: 'pg_nursing_listing',
    name: 'Nursing',
    degree: null,
    level: null,
    description: null,
    segmentId: null,
    programTypeId: null,
    path: '/programs',
    programPath: '/programs/nursing/bsn',
    status: 'pending',
    mergedInto: 'pg_nursing_bsn',
  },
  {
    id: 'pg_cyber_ms',
    name: 'Cybersecurity',
    degree: 'MS',
    level: 'graduate',
    department: 'Department of Computing',
    description:
      'Thirty-credit graduate degree covering network defence, applied cryptography, and incident response. Evening and online cohorts.',
    segmentId: 'sg_computing',
    programTypeId: 'pt_major_grad',
    path: '/programs/computing/ms-cybersecurity',
    status: 'approved',
    tags: ['tech'],
    slate: 'sp_cyber_ms',
    credentialLength: '30 credit hours',
    deliveryModes: ['online', 'hybrid'],
    concentrations: ['Network Defense', 'Digital Forensics'],
  },
  {
    id: 'pg_data_cert',
    name: 'Data Analytics',
    degree: 'Certificate',
    level: 'graduate',
    description:
      'Four-course stackable certificate that transfers in full toward the MS in Applied Data Science.',
    segmentId: 'sg_computing',
    programTypeId: 'pt_cert_grad',
    path: '/programs/computing/data-analytics-certificate',
    status: 'pending',
    flagged: true,
    tags: ['tech', 'stackable'],
  },
  {
    id: 'pg_mba_eve',
    name: 'Evening MBA',
    wbProgram: 'Professional MBA',
    degree: 'MBA',
    level: 'graduate',
    department: 'School of Business',
    description:
      'Two-year evening MBA for working professionals, with concentrations in healthcare administration and supply chain.',
    segmentId: 'sg_business',
    programTypeId: 'pt_major_grad',
    path: '/programs/business/evening-mba',
    status: 'pending',
    flagged: true,
    tags: ['business'],
    previouslyRejected: true,
    slate: 'sp_mba',
    concentrations: ['Healthcare Administration', 'Supply Chain'],
  },
  {
    id: 'pg_edu_teach',
    name: 'Elementary Education',
    degree: 'BA',
    level: 'undergraduate',
    description:
      'Undergraduate teaching degree leading to state K–6 licensure, with a full-year classroom residency.',
    segmentId: 'sg_education',
    programTypeId: 'pt_major_ug',
    path: '/programs/education/ba-elementary',
    status: 'approved',
    tags: ['education'],
    slate: 'sp_edu',
  },
  {
    id: 'pg_psych_ba',
    name: 'Psychology',
    degree: 'BA',
    level: 'undergraduate',
    description: 'Liberal-arts psychology degree with research and clinical-track options.',
    segmentId: 'sg_social',
    programTypeId: 'pt_major_ug',
    path: '/programs/psychology/ba',
    status: 'pending',
  },
  {
    id: 'pg_psych_bs',
    name: 'Psychology',
    degree: 'BS',
    level: 'undergraduate',
    description: 'Psychology with a neuroscience and statistics core.',
    segmentId: 'sg_social',
    programTypeId: 'pt_major_ug',
    path: '/programs/psychology/bs',
    status: 'approved',
  },
  {
    id: 'pg_data_viz_cert',
    name: 'Data Visualization',
    degree: 'Certificate',
    level: 'undergraduate',
    description: 'A four-course certificate in dashboards and visual analysis.',
    segmentId: null,
    programTypeId: null,
    path: '/programs/data-visualization',
    status: 'pending',
    unassigned: true,
  },
  {
    id: 'pg_psych_listing',
    name: 'Psychology',
    degree: null,
    level: 'undergraduate',
    description: null,
    segmentId: null,
    programTypeId: null,
    path: '/programs',
    programPath: '/programs/psychology/ba',
    status: 'pending',
    mergedInto: 'pg_psych_ba',
  },
  {
    id: 'pg_welding',
    name: 'Welding Technology',
    degree: 'Certificate',
    level: null,
    description:
      'Two-semester hands-on certificate. Listed under continuing education on the current site.',
    segmentId: null,
    programTypeId: null,
    path: '/ce/welding-technology',
    status: 'pending',
    slate: 'sp_welding',
  },
  {
    id: 'pg_span_minor',
    name: 'Spanish',
    degree: 'Minor',
    level: 'undergraduate',
    description: 'Eighteen-credit minor. Extracted from a course catalogue page, not a program page.',
    segmentId: null,
    programTypeId: 'pt_minor_ug',
    path: '/catalog/languages/spanish',
    programPath: null,
    status: 'rejected',
  },
  {
    id: 'pg_summer_camp',
    name: 'Summer Youth Camp',
    degree: null,
    level: null,
    description: 'Not an academic program — a summer camp listing picked up by the extractor.',
    segmentId: null,
    programTypeId: null,
    path: '/events/summer-camp',
    programPath: null,
    status: 'rejected',
  },
  {
    id: 'pg_socialwork_msw',
    name: 'Social Work',
    wbProgram: 'Social Work, advanced standing',
    degree: 'MSW',
    level: 'graduate',
    description:
      'One-year MSW for applicants holding a BSW from an accredited program. Field placement required.',
    segmentId: 'sg_social',
    programTypeId: 'pt_major_grad',
    path: '/programs/social-work/msw-advanced',
    status: 'approved',
    favorite: true,
    tags: ['health'],
    slate: 'sp_msw',
  },
  {
    id: 'pg_paralegal',
    name: 'Paralegal Studies',
    degree: 'Certificate',
    level: 'undergraduate',
    description: 'ABA-approved paralegal certificate, offered online and on campus.',
    segmentId: 'sg_legal',
    programTypeId: 'pt_cert_ug',
    path: '/programs/legal/paralegal',
    status: 'pending',
    tags: ['online'],
    deliveryModes: ['online', 'on_campus'],
  },
]

/**
 * The client's Slate list (ADR-0016). `program` is the representative a user
 * picked. Welding is disabled, so the program mapped to it is flagged; Applied
 * Data Science has no program at all.
 */
const SLATE_SEEDS: Array<{
  id: string
  slateName: string
  parsedName: string
  parsedDegree: string | null
  enabled: boolean
  program: string | null
}> = [
  {
    id: 'sp_nursing_bsn',
    slateName: 'Nursing - Bachelor of Science in Nursing',
    parsedName: 'Nursing',
    parsedDegree: 'BSN',
    enabled: true,
    program: 'pg_nursing_bsn',
  },
  {
    id: 'sp_cyber_ms',
    slateName: 'Cybersecurity - Master of Science',
    parsedName: 'Cybersecurity',
    parsedDegree: 'MS',
    enabled: true,
    program: 'pg_cyber_ms',
  },
  {
    id: 'sp_mba',
    slateName: 'Business Administration - MBA',
    parsedName: 'Business Administration',
    parsedDegree: 'MBA',
    enabled: true,
    program: null,
  },
  {
    id: 'sp_edu',
    slateName: 'Elementary Education - Bachelor of Arts',
    parsedName: 'Elementary Education',
    parsedDegree: 'BA',
    enabled: true,
    program: 'pg_edu_teach',
  },
  {
    id: 'sp_msw',
    slateName: 'Social Work - MSW',
    parsedName: 'Social Work',
    parsedDegree: 'MSW',
    enabled: true,
    program: 'pg_socialwork_msw',
  },
  {
    id: 'sp_welding',
    slateName: 'Welding Technology Certificate',
    parsedName: 'Welding Technology',
    parsedDegree: null,
    enabled: false,
    program: 'pg_welding',
  },
  {
    id: 'sp_data_science',
    slateName: 'Applied Data Science - Master of Science',
    parsedName: 'Applied Data Science',
    parsedDegree: 'MS',
    enabled: true,
    program: null,
  },
]

const slateById = new Map(SLATE_SEEDS.map((s) => [s.id, s]))
const programSeedById = new Map(PROGRAM_SEEDS.map((p) => [p.id, p]))
const siteUrl = (path: string) => `https://meridianstate.edu${path}`

/**
 * A WB program as the mock stores it: its own slate program by id, as
 * `wb_program.slate_program_id` (PC-37); the slate mapping and degree count are derived.
 */
export type WbProgramSeed = Omit<WbProgram, 'slateProgram' | 'slatePrograms' | 'programCount'> & {
  slateProgramId: string | null
}

const wbKey = (name: string) => name.trim().toLowerCase()
const wbIdFor = (name: string) => `wb_${wbKey(name).replace(/[^a-z0-9]+/g, '_')}`
const hasWbProgram = (p: ProgramSeed) => !p.unassigned && !p.mergedInto && p.level !== 'graduate'

/** One per distinct WB name of the assigned survivors, segment from its first degree. */
export const wbPrograms: WbProgramSeed[] = [
  ...new Map(
    PROGRAM_SEEDS.filter(hasWbProgram).map((p) => {
      const name = p.wbProgram ?? p.name
      return [wbIdFor(name), { id: wbIdFor(name), clientId: MERIDIAN, name, segmentId: p.segmentId,
                               slateProgramId: null as string | null,
                               createdAt: daysAgo(20, 9), updatedAt: daysAgo(3, 9) }] as const
    }),
  ).values(),
]
const wbProgramOf = (p: ProgramSeed) => (hasWbProgram(p) ? wbIdFor(p.wbProgram ?? p.name) : null)

/** Detail fields included: the adapter strips nothing, the list just ignores them. */
export const programs: ProgramDetail[] = PROGRAM_SEEDS.map((p) => {
  const slate = p.slate ? slateById.get(p.slate)! : null
  const survivor = p.mergedInto ? programSeedById.get(p.mergedInto)! : null
  return {
    id: p.id,
    clientId: MERIDIAN,
    name: p.name,
    wbProgramId: wbProgramOf(p),
    wbProgram: hasWbProgram(p) ? (p.wbProgram ?? p.name) : null,
    degree: p.degree,
    level: p.level,
    department: p.department ?? null,
    description: p.description,
    segmentId: hasWbProgram(p) ? wbPrograms.find((w) => w.id === wbProgramOf(p))!.segmentId : p.segmentId,
    programTypeId: p.programTypeId,
    programUrl: p.programPath === null ? null : siteUrl(p.programPath ?? p.path),
    sourceUrl: siteUrl(p.path),
    slateProgram: slate ? { id: slate.id, slateName: slate.slateName, enabled: slate.enabled } : null,
    mergedInto: survivor ? { id: survivor.id, name: survivor.name } : null,
    status: p.status,
    favorite: p.favorite ?? false,
    flagged: p.flagged ?? false,
    tags: p.tags ?? [],
    entityHash: hashFor(p.id),
    verdicts: verdictsFor(p.id, p.status, daysAgo(3, 14)),
    previouslyRejected: p.previouslyRejected ?? false,
    source: 'crawl',
    notes: null,
    credentialLength: p.credentialLength ?? null,
    cipCode: null,
    deliveryModes: p.deliveryModes ?? [],
    concentrations: p.concentrations ?? [],
    pages: [{ id: `page_${p.id}`, url: siteUrl(p.path), title: p.name, lastSeenAt: daysAgo(5, 11) }],
    // Derived from mergedInto by the adapter, so the two cannot disagree.
    merged: [],
  }
})

/**
 * The representative and the WB program are stored by id, as
 * `slate_program.program_id` and `wb_program_id` are. Each maps to its
 * representative's WB program, as the PC-32 backfill maps it.
 */
export interface SlateProgramSeed extends Omit<SlateProgram, 'program' | 'mappedPrograms' | 'wbProgram'> {
  programId: string | null
  wbProgramId: string | null
}

export const slatePrograms: SlateProgramSeed[] = SLATE_SEEDS.map((s) => {
  const representative = s.program ? programSeedById.get(s.program) : undefined
  return {
    id: s.id,
    clientId: MERIDIAN,
    slateName: s.slateName,
    parsedName: s.parsedName,
    parsedDegree: s.parsedDegree,
    enabled: s.enabled,
    programId: s.program,
    wbProgramId: representative ? wbProgramOf(representative) : null,
  }
})

/**
 * The slate program a WB program with none takes, as migration 036's backfill:
 * of the enabled slate programs mapped to it, the one named like it, then the
 * first by name.
 */
export function pickSlateProgram(wb: { id: string; name: string }, slates: SlateProgramSeed[]): string | null {
  const same = (s: SlateProgramSeed) => s.slateName.trim().toLowerCase() === wb.name.trim().toLowerCase()
  const [chosen] = slates
    .filter((s) => s.enabled && s.wbProgramId === wb.id)
    .sort((a, b) => Number(same(b)) - Number(same(a)) || a.slateName.toLowerCase().localeCompare(b.slateName.toLowerCase()))
  return chosen?.id ?? null
}

for (const w of wbPrograms) w.slateProgramId = pickSlateProgram(w, slatePrograms)

/* ---------------------------------------------------------------- quotes */

interface QuoteSeed {
  id: string
  text: string
  speakerName?: string
  classYear?: string
  majors?: string
  currentPosition?: string
  hometown?: string
  path: string | null
  status: ReviewStatus
  favorite?: boolean
  tags?: string[]
  previouslyRejected?: boolean
  context?: string
  quoteDate?: string
  dateProvenance?: DateProvenance
  majorMinorMentioned?: string
}

const QUOTE_SEEDS: QuoteSeed[] = [
  {
    id: 'qt_001',
    text: 'I finished my BSN while working nights. The clinical coordinator built my placement around my shifts — nobody else would do that.',
    speakerName: 'Dani Okafor',
    classYear: '’23',
    majors: 'Nursing',
    currentPosition: 'RN, cardiac ICU at Northbay',
    hometown: 'Riverton',
    path: '/stories/dani-okafor',
    status: 'approved',
    favorite: true,
    tags: ['health', 'testimonial'],
    context:
      'Dani Okafor came to Meridian after six years as a certified nursing assistant. “I finished my BSN while working nights…” She now works in the cardiac ICU at Northbay.',
    quoteDate: '2024-03',
    dateProvenance: 'explicit',
    majorMinorMentioned: 'BSN',
  },
  {
    id: 'qt_002',
    text: 'We rebuilt the cybersecurity curriculum around what employers said they could not hire for. Two years later every graduate had an offer before commencement.',
    speakerName: 'Dr. Priya Raman',
    currentPosition: 'Chair, School of Computing',
    path: '/news/cybersecurity-curriculum-refresh',
    status: 'approved',
    tags: ['tech', 'faculty'],
    quoteDate: '2025',
    dateProvenance: 'url_segment',
    majorMinorMentioned: 'cybersecurity',
  },
  {
    id: 'qt_003',
    text: 'The evening MBA was the only one within an hour of here that did not make me choose between a promotion and a degree.',
    speakerName: 'Marcus Feld',
    classYear: 'MBA candidate',
    majors: 'Business Administration',
    path: '/programs/business/evening-mba',
    status: 'pending',
    tags: ['business'],
  },
  {
    id: 'qt_004',
    text: 'My residency year was in the same district I grew up in. I had a classroom of my own before I had a diploma.',
    speakerName: 'Aisha Bell',
    classYear: '’24',
    majors: 'Elementary Education',
    path: '/stories/aisha-bell',
    status: 'pending',
    tags: ['education', 'testimonial'],
    quoteDate: '2024',
    dateProvenance: 'grad_year',
  },
  {
    id: 'qt_005',
    text: 'Financial aid walked me through every line of the offer. I did not sign anything I did not understand.',
    speakerName: 'R. Alvarez',
    currentPosition: 'Parent',
    path: '/admissions/affordability',
    status: 'pending',
  },
  {
    id: 'qt_006',
    text: 'Apply by March 1 for priority consideration.',
    path: '/admissions/deadlines',
    status: 'rejected',
    context: 'Extracted from a deadline callout box. Not a quote — no speaker, no claim.',
  },
  {
    id: 'qt_007',
    text: 'Meridian graduates stay. Seventy-one percent of them are still living in the region five years out, which is the number the county actually cares about.',
    speakerName: 'Tom Greer',
    currentPosition: 'President',
    path: '/about/presidents-letter',
    status: 'pending',
    tags: ['leadership'],
    previouslyRejected: true,
  },
  {
    id: 'qt_008',
    text: 'The paralegal certificate paid for itself in four months. I was billing at a higher rate before I finished the last course.',
    speakerName: 'Ken Mihara',
    classYear: '’24',
    majors: 'Paralegal Studies',
    path: '/stories/ken-mihara',
    status: 'approved',
    tags: ['testimonial'],
  },
  {
    id: 'qt_009',
    text: 'Advising is not a once-a-semester thing here. My advisor texted me the week registration opened.',
    speakerName: 'Jules Tanaka',
    classYear: 'third year',
    majors: 'Psychology',
    // Its page URL matched no crawled page: no source page.
    path: null,
    status: 'pending',
  },
]

export const quotes: QuoteDetail[] = QUOTE_SEEDS.map((q) => ({
  id: q.id,
  clientId: MERIDIAN,
  text: q.text,
  speakerName: q.speakerName ?? null,
  classYear: q.classYear ?? null,
  majors: q.majors ?? null,
  currentPosition: q.currentPosition ?? null,
  hometown: q.hometown ?? null,
  context: q.context ?? null,
  quoteDate: q.quoteDate ?? null,
  dateProvenance: q.dateProvenance ?? null,
  sourceUrl: q.path ? `https://meridianstate.edu${q.path}` : null,
  // One quote with an image, so a featured quote's image shows (PC-9).
  studentImageUrl: q.id === 'qt_001' ? 'https://meridianstate.edu/stories/dani-okafor.jpg' : null,
  majorMinorMentioned: q.majorMinorMentioned ?? null,
  notes: null,
  substance: null,
  programs: [],
  status: q.status,
  favorite: q.favorite ?? false,
  flagged: false,
  tags: q.tags ?? [],
  entityHash: hashFor(q.id),
  verdicts: verdictsFor(q.id, q.status, daysAgo(3, 15)),
  previouslyRejected: q.previouslyRejected ?? false,
  source: 'crawl',
}))

/* ----------------------------------------------------------------- media */

interface MediaSeed {
  id: string
  kind: 'image' | 'video'
  title?: string
  description: string | null
  path: string
  status: ReviewStatus
  favorite?: boolean
  tags?: string[]
  /** Fill colour of the generated placeholder image. */
  poster: string
  width?: number
  height?: number
  /** A video's URL. Images get a generated placeholder instead. */
  url?: string
  youtubeId?: string
  usable?: boolean
  /** The hotlink 404s, to show the broken-media placeholder. */
  broken?: boolean
  previouslyRejected?: boolean
}

/** A flat-colour SVG, so the mock renders real <img> tags without a network. */
function placeholderImage(colour: string, width = 1600, height = 900): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="${colour}"/></svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

const MEDIA_SEEDS: MediaSeed[] = [
  {
    id: 'md_001',
    kind: 'image',
    title: 'Simulation lab',
    description: 'Nursing student in blue scrubs practising an IV insertion on a simulation manikin.',
    path: '/programs/nursing/bsn',
    status: 'approved',
    favorite: true,
    tags: ['health', 'students'],
    poster: '#2d4a63',
    width: 1600,
    height: 900,
  },
  {
    id: 'md_002',
    kind: 'image',
    description: 'Aerial view of the main quad in early autumn, students crossing the central path.',
    path: '/about/campus',
    status: 'approved',
    tags: ['campus'],
    poster: '#4a5c3a',
  },
  {
    id: 'md_003',
    kind: 'video',
    title: 'Campus tour',
    description: 'Ninety-second campus tour narrated by two current students.',
    path: '/admissions/visit',
    status: 'pending',
    tags: ['campus', 'tour'],
    poster: '#3b3b52',
    url: 'https://www.youtube.com/watch?v=aqz-KE-bpKQ',
    youtubeId: 'aqz-KE-bpKQ',
  },
  {
    id: 'md_004',
    kind: 'image',
    description: 'Cybersecurity lab — six workstations, students at a tabletop incident exercise.',
    path: '/programs/computing/ms-cybersecurity',
    status: 'pending',
    tags: ['tech'],
    poster: '#1f3b4d',
  },
  {
    id: 'md_005',
    kind: 'image',
    description: 'Commencement, graduates throwing caps.',
    path: '/news/commencement-2024',
    status: 'pending',
    poster: '#5a4636',
    broken: true,
  },
  {
    id: 'md_006',
    kind: 'video',
    description: 'Dr. Priya Raman on why the computing curriculum was rebuilt. Interview, two minutes.',
    path: '/news/cybersecurity-curriculum-refresh',
    status: 'approved',
    tags: ['faculty', 'tech'],
    poster: '#33414f',
    url: 'https://vimeo.com/76979871',
  },
  {
    id: 'md_007',
    kind: 'image',
    description: 'Stock photo of an unidentified lecture hall. Not Meridian.',
    path: '/programs/psychology/ba',
    status: 'rejected',
    poster: '#6b6b6b',
  },
  {
    id: 'md_008',
    kind: 'image',
    description: 'Education students during the classroom residency, working with a small reading group.',
    path: '/programs/education/ba-elementary',
    status: 'pending',
    tags: ['education', 'students'],
    poster: '#4d5b47',
    previouslyRejected: true,
  },
  {
    id: 'md_009',
    kind: 'image',
    description: null,
    path: '/',
    status: 'pending',
    poster: '#9a2a2a',
    width: 240,
    height: 80,
    usable: false,
  },
]

export const media: MediaDetail[] = MEDIA_SEEDS.map((m) => ({
  id: m.id,
  clientId: MERIDIAN,
  kind: m.kind,
  url:
    m.url ??
    (m.broken ? `https://meridianstate.invalid/assets/${m.id}.jpg` : placeholderImage(m.poster, m.width, m.height)),
  youtubeId: m.youtubeId ?? null,
  title: m.title ?? null,
  description: m.description,
  width: m.width ?? null,
  height: m.height ?? null,
  usable: m.usable ?? true,
  sourceUrl: `https://meridianstate.edu${m.path}`,
  programs: [],
  status: m.status,
  favorite: m.favorite ?? false,
  flagged: false,
  tags: m.tags ?? [],
  entityHash: hashFor(m.id),
  verdicts: verdictsFor(m.id, m.status, daysAgo(2, 16)),
  previouslyRejected: m.previouslyRejected ?? false,
  source: 'crawl',
}))

/* --------------------------------------------------------- partner facts */

interface FactSeed {
  id: string
  kind: 'standard' | 'additional'
  label: string
  value: string
  path: string | null
  status: ReviewStatus
  tags?: string[]
  previouslyRejected?: boolean
}

const FACT_SEEDS: FactSeed[] = [
  { id: 'ft_found', kind: 'standard', label: 'Founded', value: '1911', path: '/about', status: 'approved' },
  {
    id: 'ft_enroll',
    kind: 'standard',
    label: 'Total enrolment',
    value: '14,280 (fall 2024)',
    path: '/about/facts',
    status: 'approved',
  },
  {
    id: 'ft_ratio',
    kind: 'standard',
    label: 'Student–faculty ratio',
    value: '17:1',
    path: '/about/facts',
    status: 'pending',
  },
  {
    id: 'ft_accred',
    kind: 'standard',
    label: 'Accreditation',
    value: 'Higher Learning Commission; CCNE (nursing); ABA (paralegal)',
    path: '/about/accreditation',
    status: 'pending',
  },
  {
    id: 'ft_tuition',
    kind: 'standard',
    label: 'In-state tuition',
    value: '$9,460 per year, 2024–25',
    path: '/admissions/affordability',
    status: 'pending',
  },
  {
    id: 'ft_retain',
    kind: 'additional',
    label: 'First-year retention',
    value: '84% of first-time full-time students return for a second year.',
    path: '/about/facts',
    status: 'approved',
    tags: ['outcomes'],
  },
  {
    id: 'ft_region',
    kind: 'additional',
    label: 'Regional retention of graduates',
    value: '71% still live in the region five years after graduating.',
    path: '/about/presidents-letter',
    status: 'pending',
    tags: ['outcomes'],
  },
  {
    id: 'ft_nclex',
    kind: 'additional',
    label: 'NCLEX-RN first-time pass rate',
    value: '93% for the 2024 cohort, against a national average of 88%.',
    path: '/programs/nursing/bsn',
    status: 'pending',
    tags: ['outcomes', 'health'],
    previouslyRejected: true,
  },
  {
    id: 'ft_aid',
    kind: 'additional',
    label: 'Students receiving aid',
    value: '82% receive some form of financial aid.',
    path: '/admissions/affordability',
    status: 'pending',
  },
  {
    id: 'ft_parking',
    kind: 'additional',
    label: 'Parking permits',
    value: 'Annual commuter permit is $180.',
    path: '/campus/parking',
    status: 'rejected',
  },
]

export const facts: PartnerFact[] = FACT_SEEDS.map((f) => ({
  id: f.id,
  clientId: MERIDIAN,
  kind: f.kind,
  label: f.label,
  value: f.value,
  sourceUrl: f.path ? `https://meridianstate.edu${f.path}` : null,
  status: f.status,
  favorite: false,
  flagged: false,
  tags: f.tags ?? [],
  entityHash: hashFor(f.id),
  verdicts: verdictsFor(f.id, f.status, daysAgo(2, 17)),
  previouslyRejected: f.previouslyRejected ?? false,
  source: 'crawl',
}))

/* ---------------------------------------------------------- copy library */

export const copyDocs: CopyDoc[] = [
  {
    id: 'cp_001',
    clientId: MERIDIAN,
    name: 'RN-to-BSN landing intro',
    copyType: 'landing',
    body: `Finish the degree without leaving the floor.

Meridian's RN-to-BSN completion is built for nurses already working — twelve to eighteen months, fully online, with clinical hours you can schedule around your shifts.

Transfer credit is evaluated before you pay anything. Most students arrive with more of the degree already done than they expected.`,
    tags: ['health', 'online'],
    favorite: true,
    author: 'Claude',
    createdAt: daysAgo(9),
    updatedAt: daysAgo(4),
    version: 3,
    origin: 'mcp',
    history: [
      {
        version: 1,
        body: 'Finish your degree online. Meridian\'s RN-to-BSN is designed for working nurses.',
        savedAt: daysAgo(9),
        author: 'Claude',
      },
      {
        version: 2,
        body: `Finish the degree without leaving the floor.

Meridian's RN-to-BSN completion is built for nurses already working — twelve to eighteen months, fully online.`,
        savedAt: daysAgo(7),
        author: 'Claude',
      },
      {
        version: 3,
        body: `Finish the degree without leaving the floor.

Meridian's RN-to-BSN completion is built for nurses already working — twelve to eighteen months, fully online, with clinical hours you can schedule around your shifts.

Transfer credit is evaluated before you pay anything. Most students arrive with more of the degree already done than they expected.`,
        savedAt: daysAgo(4),
        author: 'Claude',
      },
    ],
  },
  {
    id: 'cp_002',
    clientId: MERIDIAN,
    name: 'Priority deadline reminder email',
    copyType: 'email',
    body: `Subject: Two weeks to the priority deadline

You started an application to Meridian. It is still open, and the priority deadline is 1 March.

Priority applicants are considered first for institutional scholarships — that is the whole difference. There is no separate scholarship form.

Pick up where you left off: [link]`,
    tags: ['admissions'],
    favorite: false,
    author: 'Joe',
    createdAt: daysAgo(6),
    updatedAt: daysAgo(6),
    version: 1,
    origin: 'manual',
    history: [
      {
        version: 1,
        body: `Subject: Two weeks to the priority deadline

You started an application to Meridian. It is still open, and the priority deadline is 1 March.

Priority applicants are considered first for institutional scholarships — that is the whole difference. There is no separate scholarship form.

Pick up where you left off: [link]`,
        savedAt: daysAgo(6),
        author: 'Joe',
      },
    ],
  },
  {
    id: 'cp_003',
    clientId: MERIDIAN,
    name: 'Cybersecurity MS — social post set',
    copyType: 'social',
    body: `1/ We rebuilt the MS in Cybersecurity around what regional employers said they could not hire for.

2/ Every graduate in the last two cohorts had an offer before commencement.

3/ Evening and online sections. Thirty credits. Next cohort starts in August.`,
    tags: ['tech'],
    favorite: false,
    author: 'Claude',
    createdAt: daysAgo(3),
    updatedAt: daysAgo(3),
    version: 1,
    origin: 'mcp',
    history: [
      {
        version: 1,
        body: `1/ We rebuilt the MS in Cybersecurity around what regional employers said they could not hire for.

2/ Every graduate in the last two cohorts had an offer before commencement.

3/ Evening and online sections. Thirty credits. Next cohort starts in August.`,
        savedAt: daysAgo(3),
        author: 'Claude',
      },
    ],
  },
]

/* ----------------------------------------------------------------- crawl */

export const crawlRuns: CrawlRun[] = [
  {
    id: 'run_meridian_03',
    clientId: MERIDIAN,
    startedAt: daysAgo(33, 6),
    finishedAt: daysAgo(33, 7),
    status: 'complete',
    found: 388,
    crawled: 371,
    failed: 5,
    skipped: 12,
  },
  {
    id: 'run_meridian_04',
    clientId: MERIDIAN,
    startedAt: daysAgo(2, 6),
    finishedAt: daysAgo(2, 7),
    status: 'complete',
    found: 412,
    crawled: 398,
    failed: 6,
    skipped: 8,
  },
  {
    id: 'run_northbay_02',
    clientId: NORTHBAY,
    startedAt: daysAgo(19, 4),
    finishedAt: daysAgo(19, 5),
    status: 'complete',
    found: 144,
    crawled: 142,
    failed: 0,
    skipped: 2,
  },
  {
    id: 'run_cedarwood_01',
    clientId: CEDARWOOD,
    startedAt: daysAgo(5, 11),
    finishedAt: daysAgo(5, 12),
    status: 'complete',
    found: 96,
    crawled: 88,
    failed: 8,
    skipped: 0,
  },
]

interface PageSeed {
  path: string
  type: CrawledPage['pageType']
  importance: number
  changed?: boolean
  excluded?: boolean
  error?: string
}

const PAGE_SEEDS: PageSeed[] = [
  { path: '/programs/nursing/bsn', type: 'program', importance: 0.98, changed: true },
  { path: '/programs/nursing/rn-to-bsn', type: 'program', importance: 0.94 },
  { path: '/programs/computing/ms-cybersecurity', type: 'program', importance: 0.93, changed: true },
  { path: '/programs/computing/data-analytics-certificate', type: 'program', importance: 0.81 },
  { path: '/programs/business/evening-mba', type: 'program', importance: 0.9, changed: true },
  { path: '/programs/education/ba-elementary', type: 'program', importance: 0.86 },
  { path: '/programs/psychology/ba', type: 'program', importance: 0.72 },
  { path: '/programs/social-work/msw-advanced', type: 'program', importance: 0.84 },
  { path: '/programs/legal/paralegal', type: 'program', importance: 0.79 },
  { path: '/admissions/affordability', type: 'landing', importance: 0.88 },
  { path: '/admissions/deadlines', type: 'landing', importance: 0.7 },
  { path: '/admissions/visit', type: 'landing', importance: 0.66 },
  { path: '/about', type: 'landing', importance: 0.75 },
  { path: '/about/facts', type: 'landing', importance: 0.82, changed: true },
  { path: '/about/accreditation', type: 'landing', importance: 0.64 },
  { path: '/about/presidents-letter', type: 'article', importance: 0.58 },
  { path: '/about/campus', type: 'media', importance: 0.55 },
  { path: '/stories/dani-okafor', type: 'testimonial', importance: 0.77 },
  { path: '/stories/aisha-bell', type: 'testimonial', importance: 0.74 },
  { path: '/stories/ken-mihara', type: 'testimonial', importance: 0.71 },
  { path: '/news/cybersecurity-curriculum-refresh', type: 'article', importance: 0.69, changed: true },
  { path: '/news/commencement-2024', type: 'article', importance: 0.41 },
  { path: '/student-life/advising', type: 'other', importance: 0.47 },
  { path: '/ce/welding-technology', type: 'program', importance: 0.35 },
  { path: '/catalog/languages/spanish', type: 'other', importance: 0.22 },
  { path: '/campus/parking', type: 'other', importance: 0.11 },
  { path: '/events/summer-camp', type: 'other', importance: 0.08, excluded: true },
  {
    path: '/programs/engineering/bs-civil',
    type: 'program',
    importance: 0.8,
    error: '504 Gateway Timeout after 3 attempts',
  },
  {
    path: '/programs/engineering/bs-mechanical',
    type: 'program',
    importance: 0.8,
    error: '504 Gateway Timeout after 3 attempts',
  },
  {
    path: '/library/special-collections',
    type: 'other',
    importance: 0.3,
    error: 'robots.txt disallow',
  },
  {
    path: '/apply',
    type: 'landing',
    importance: 0.92,
    error: 'Redirect loop (apply.meridianstate.edu)',
  },
]

export const pages: CrawledPage[] = PAGE_SEEDS.map((p, i) => ({
  id: `pgq_${String(i).padStart(3, '0')}`,
  clientId: MERIDIAN,
  url: `https://meridianstate.edu${p.path}`,
  pageType: p.type,
  importance: p.importance,
  changed: p.changed ?? false,
  excluded: p.excluded ?? false,
  error: p.error ?? null,
}))
